"use client"

import { Button } from "@heroui/button"
import { AnimatePresence, motion } from "framer-motion"
import { PiHandGrabbing, PiHandGrabbingFill, PiMinusBold } from "react-icons/pi"
import { useState, useRef, useEffect, useMemo } from "react"
import { useStoreLive } from "@/app/store/slices/live/hook"
import { Card, CardBody, CardHeader } from "@heroui/card"
import { Slider } from "@heroui/slider"
import { FaExpand, FaPause, FaPlay, FaSpinner, FaVolumeMute, FaVolumeUp } from "react-icons/fa"
import { time } from "./audio"
import clsx from "clsx"
import { Divider } from "@heroui/divider"

const stream = process.env.NEXT_PUBLIC_STREAM || "http://localhost:8080/hls"

export function VideoCardFloat() {
  const { video: { expand, toggle, drag } } = useStoreLive();

  if (!expand) return null;

  return (
    <motion.div
      layoutId="video_player"
      initial={{ opacity: 0, scale: 0.8, y: 50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 50 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="z-[9999] pointer-events-auto"
    >
      <Card
        className="w-[340px] sm:w-[420px] bg-black border border-[#E31D2B]/20 shadow-2xl overflow-hidden"
        radius="lg"
      >
        <CardHeader className="flex justify-between items-center px-4 py-3 bg-zinc-950/50">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E31D2B] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E31D2B]"></span>
            </span>
            <span className="text-[10px] font-bold text-white tracking-widest uppercase select-none">
              Direct TV
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              className="bg-white/5 text-white hover:bg-[#E31D2B] transition-colors h-7 w-7 min-w-0"
              onPress={() => toggle("expand")}
            >
              <PiMinusBold size={12} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              className={clsx(
                "h-7 w-7 min-w-0 transition-colors",
                drag ? "bg-[#E31D2B] text-white" : "bg-white/5 text-white hover:bg-danger"
              )}
              onPress={() => toggle("drag")}
            >
              {drag ? <PiHandGrabbingFill size={14} /> : <PiHandGrabbing size={14} />}
            </Button>
          </div>
        </CardHeader>

        <Divider className="bg-white/10" />

        <CardBody className="p-0 relative aspect-video bg-black overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center opacity-20">
             <div className="w-8 h-8 border-2 border-[#E31D2B] border-t-transparent rounded-full animate-spin" />
          </div>

          <VideoPlayer />
        </CardBody>
      </Card>
    </motion.div>
  );
}
export default function VideoPlayer() {
  
  const playerRef = useRef<HTMLVideoElement>(null);
  const { video: { video: isLive } } = useStoreLive(); 
  
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const src = useMemo(() => isLive ? `${stream}/stream/index.m3u8` : void 0, [isLive]);

  useEffect(() => {
    if (!isLive) {
      setStatus('idle');
      return;
    }

    setStatus('loading');

    const timer = setTimeout(() => {
      if (playerRef.current) {
        // Le playerRef de ReactHlsPlayer donne accès à l'élément vidéo
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isLive]);

  const hlsConfig = useMemo(() => ({
    enableWorker: true,
    lowLatencyMode: true,
    backBufferLength: 0,
    manifestLoadingMaxRetry: 20, 
    manifestLoadingRetryDelay: 1000,
    xhrSetup: (xhr: XMLHttpRequest) => {
      xhr.addEventListener('readystatechange', (e) => {
        if (xhr.readyState === 4 && xhr.status === 404) {
          
        }
      });
    }
  }), []);

  const handleVideoReady = () => {
    setStatus('ready');
  };

  const togglePlay = () => {
    if (status !== 'ready') return;
    if (playerRef.current?.paused) {
      playerRef.current.play();
    } else {
      playerRef.current?.pause();
    }
  };

  return (
    <div
      className="relative w-full h-full group bg-black overflow-hidden flex items-center justify-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence>
        {(status === 'loading' || !isLive) && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[40] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-4"
          >
            <FaSpinner className="animate-spin text-[#E31D2B]" size={40} />
            <p className="text-white text-xs font-bold uppercase tracking-widest animate-pulse">
              {!isLive ? "En attente du signal..." : "Préparation du flux direct..."}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="w-full h-full">
        {/* {
          !!src &&
          <ReactHlsPlayer
            key={Date.now()}
            playerRef={playerRef}
            src={src}
            autoPlay={true} 
            controls={false}
            width="100%"
            height="100%"
            className={clsx("w-full h-full object-contain transition-opacity duration-1000", {
              "opacity-100": status === 'ready',
              "opacity-0": status !== 'ready'
            })}
            onClick={togglePlay}
            onCanPlay={handleVideoReady}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            hlsConfig={hlsConfig}
          />
        } */}
      </div>
      <div className={clsx(
        "absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent flex flex-col justify-end px-4 pb-4 pt-12 transition-all duration-300 z-20 pointer-events-none",
        (isHovered || !isPlaying) && status === 'ready' ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}>
        <div className="flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-4">
            <Button
              isIconOnly
              onPress={togglePlay}
              className="bg-transparent text-white hover:text-[#E31D2B] min-w-0"
              isDisabled={status !== 'ready'}
            >
              {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
            </Button>

            <div className="flex items-center gap-2 group/volume">
              <button 
                onClick={() => {
                  if (playerRef.current) {
                    const m = !isMuted;
                    playerRef.current.muted = m;
                    setIsMuted(m);
                  }
                }} 
                className="text-white hover:text-white/80"
              >
                {isMuted || volume === 0 ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
              </button>
              <div className="w-0 overflow-hidden group-hover/volume:w-24 transition-all duration-300 ease-out">
                <Slider
                  size="sm"
                  maxValue={1}
                  minValue={0}
                  step={0.01}
                  value={isMuted ? 0 : volume}
                  onChange={(v) => {
                    const val = Array.isArray(v) ? v[0] : v;
                    if (playerRef.current) {
                      playerRef.current.volume = val;
                      setVolume(val);
                    }
                  }}
                  classNames={{ filler: "bg-white", thumb: "bg-white" }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-white/10 border border-white/10 backdrop-blur-md">
              <div className={clsx("w-2 h-2 rounded-full", isLive ? "bg-[#E31D2B] animate-pulse" : "bg-zinc-500")} />
              <span className="text-[10px] font-bold text-white uppercase tracking-wider">
                {status === 'ready' ? "En Direct" : "Hors Ligne"}
              </span>
            </div>

            <Button
              isIconOnly
              className="bg-transparent text-white/80 hover:text-white min-w-0"
              onPress={() => playerRef.current?.requestFullscreen()}
              isDisabled={status !== 'ready'}
            >
              <FaExpand size={16} />
            </Button>
          </div>
        </div>
      </div>

      {status === 'ready' && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center cursor-pointer z-30" onClick={togglePlay}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="w-16 h-16 bg-[#E31D2B] rounded-full flex items-center justify-center shadow-2xl shadow-red-900/50 hover:scale-110 transition-transform"
          >
            <FaPlay className="text-white ml-1" size={26} />
          </motion.div>
        </div>
      )}
    </div>
  );
}